import * as THREE from 'three';
import { ATMO_GLSL, atmoUniforms } from '../atmosphere';

// CPU-simulated, GPU-drawn billboard particles. Soft (depth-faded against
// the opaque scene), lit by the sun/sky with forward scattering, and fogged
// by the shared atmosphere.

export const PKind = { Spray: 0, Mist: 1, Smoke: 2, Flash: 3, Spark: 4, Droplet: 5, Flame: 6, Debris: 7, Ember: 8, Soot: 9 } as const;
export type PKind = (typeof PKind)[keyof typeof PKind];

interface P {
  kind: PKind;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  age: number;
  life: number;
  size0: number;
  size1: number;
  alpha: number;
  rot: number;
  rotV: number;
  drag: number;
  gravity: number;
  cell: number;
  alive: boolean;
  tr: number;
  tg: number;
  tb: number;
}

/** Default colour (HDR for the glowing kinds) of each particle kind. */
const TINTS: Record<number, [number, number, number]> = {
  [PKind.Flash]: [60, 33, 13.2],
  [PKind.Spark]: [60, 33, 13.2],
  [PKind.Flame]: [26, 10, 2.4],
  [PKind.Ember]: [40, 16, 4],
  [PKind.Debris]: [0.42, 0.3, 0.2],
  [PKind.Soot]: [0.16, 0.145, 0.13],
};
const WHITE: [number, number, number] = [1, 1, 1];

const VERT = /* glsl */ `
in vec4 aPosSize;
in vec4 aParams;   // alpha, rotation, atlas cell, stretch
in vec3 aVel;
in vec3 aTint;
out vec3 vTint;
out vec2 vUv;
out float vAlpha;
out vec3 vWorld;
out float vViewZ;
flat out float vCell;
void main() {
  vec3 center = aPosSize.xyz;
  float size = aPosSize.w;
  vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 camUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  float c = cos(aParams.y), s = sin(aParams.y);
  vec2 q = vec2(position.x * c - position.y * s, position.x * s + position.y * c);
  vec3 wp = center + (camRight * q.x + camUp * q.y) * size;
  // Fast droplets stretch along their motion.
  if (aParams.w > 0.0) {
    vec3 v = aVel;
    float sp = length(v);
    if (sp > 0.01) {
      vec3 d = v / sp;
      vec3 side = normalize(cross(d, normalize(cameraPosition - center)));
      wp = center + side * position.x * size + d * position.y * size * (1.0 + sp * aParams.w);
    }
  }
  vUv = position.xy + 0.5;
  vTint = aTint;
  vAlpha = aParams.x;
  vCell = aParams.z;
  vWorld = wp;
  vec4 mv = viewMatrix * vec4(wp, 1.0);
  vViewZ = -mv.z;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
${ATMO_GLSL}
uniform sampler2D uAtlas;
uniform sampler2D uDepth;
uniform vec2 uRes;
uniform vec2 uNearFar;
uniform float uAdditive;
in vec2 vUv;
in vec3 vTint;
in float vAlpha;
in vec3 vWorld;
in float vViewZ;
flat in float vCell;
float viewZ(float d) {
  float n = uNearFar.x, f = uNearFar.y;
  return (n * f) / ((f - n) * d - f);
}
void main() {
  // Canvas textures are flipped: the atlas's top row is at v = 0.5..1.
  vec2 cell = vec2(mod(vCell, 4.0), 1.0 - floor(vCell / 4.0));
  vec4 t = texture(uAtlas, (vUv + cell) * vec2(0.25, 0.5));
  float sceneZ = -viewZ(texture(uDepth, gl_FragCoord.xy / uRes).r);
  float soft = clamp((sceneZ - vViewZ) / 0.8, 0.0, 1.0);
  float a = t.a * vAlpha * soft;
  if (a < 0.003) discard;
  vec3 V = normalize(cameraPosition - vWorld);
  if (uAdditive > 0.5) {
    // Emissive: flash and sparks (already HDR in the tint).
    vec3 col = t.rgb * vTint;
    float fog = exp(-length(vWorld - cameraPosition) * 0.0005);
    gl_FragColor = vec4(col * a * fog, 0.0);
    return;
  }
  // Water spray and smoke scatter strongly forward.
  float mu = dot(-V, uSunDir);
  float phase = 0.35 + 1.6 * pow(max(mu, 0.0), 6.0);
  float shade = mix(0.55, 1.0, t.r);
  vec3 light = uSunColor * atmoCloudShadow(vWorld) * phase * 0.3 + uSkyAmbient * 0.42;
  vec3 col = t.rgb * light * shade * vTint;
  col += vec3(0.75, 0.8, 1.0) * uFlash.w * 0.3;
  col = applyAtmosphere(col, vWorld - cameraPosition);
  gl_FragColor = vec4(col, a);
}
`;

function makeAtlas(): THREE.Texture {
  const S = 128;
  const c = document.createElement('canvas');
  c.width = S * 4;
  c.height = S * 2;
  const g = c.getContext('2d')!;
  const img = g.createImageData(S * 4, S * 2);
  const noise = (x: number, y: number, f: number) => {
    let v = 0, a = 0.5;
    for (let o = 0; o < 4; o++) {
      v += a * (0.5 + 0.5 * Math.sin(x * f * 1.7 + Math.sin(y * f * 1.3 + o) * 2 + o * 3.1) * Math.cos(y * f * 1.9 - Math.sin(x * f * 0.7) + o));
      a *= 0.5;
      f *= 2.1;
    }
    return v;
  };
  // A jagged shard outline for debris.
  const shard: [number, number][] = [];
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const r = i % 2 === 0 ? 0.95 : 0.35 + (i % 3) * 0.12;
    shard.push([Math.cos(a) * r * 0.45, Math.sin(a) * r]);
  }
  const inShard = (u: number, v: number) => {
    let inside = false;
    for (let i = 0, j = shard.length - 1; i < shard.length; j = i++) {
      const [xi, yi] = shard[i]!, [xj, yj] = shard[j]!;
      if (yi > v !== yj > v && u < ((xj - xi) * (v - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  for (let cell = 0; cell < 8; cell++) {
    const ox = (cell % 4) * S, oy = Math.floor(cell / 4) * S;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const u = (x + 0.5) / S * 2 - 1, v = (y + 0.5) / S * 2 - 1;
        const r = Math.hypot(u, v);
        let a = 0, shade = 1;
        if (cell === 0) {
          // Spray: clumpy droplets.
          const n = noise(u * 3, v * 3, 3);
          a = Math.max(0, 1 - r) ** 1.5 * (0.35 + 0.9 * n);
          shade = 0.8 + 0.2 * n;
        } else if (cell === 1) {
          // Smoke / mist: billowy.
          const n = noise(u * 2 + 5, v * 2 + 1, 2);
          a = Math.max(0, 1 - r * (0.8 + 0.4 * n)) ** 1.2 * (0.5 + 0.6 * n);
          shade = 0.6 + 0.4 * (1 - v * 0.5) * n;
        } else if (cell === 2) {
          // Droplet: small bright core.
          a = Math.max(0, 1 - r * 1.8) ** 2;
          shade = 1;
        } else if (cell === 3) {
          // Flash / spark: hot core with rays.
          const ang = Math.atan2(v, u);
          const rays = 0.5 + 0.5 * Math.pow(Math.abs(Math.cos(ang * 3)), 8);
          a = Math.max(0, 1 - r) ** 3 * (0.6 + 0.8 * rays);
          shade = 1;
        } else if (cell === 4) {
          // Flame tongue: a teardrop, licked ragged at the top.
          const n = noise(u * 2.5 + 3, v * 2.5, 2.5);
          const w = 0.55 * Math.sqrt(Math.max(0, (v + 1) / 2)) * (1.1 - Math.max(0, v) * 0.9);
          const dx = Math.abs(u) / Math.max(0.05, w);
          a = Math.max(0, 1 - dx) ** 1.2 * Math.max(0, 1 - Math.abs(v + 0.1) * 1.05) * (0.55 + 0.7 * n);
          shade = 0.75 + 0.5 * (1 - (v + 1) / 2);
        } else if (cell === 5) {
          // Debris shard: solid, with a grain.
          a = inShard(u, v) ? 1 : 0;
          shade = 0.65 + 0.35 * (0.5 + 0.5 * Math.sin(v * 22 + u * 4));
        } else if (cell === 6) {
          // Ember: a small hot point.
          a = Math.max(0, 1 - r * 2.6) ** 1.5;
          shade = 1;
        } else {
          // Thick smoke: dense, lumpy.
          const n = noise(u * 2.2 + 11, v * 2.2 + 7, 2.2);
          a = Math.min(1, Math.max(0, 1 - r * (0.75 + 0.5 * n)) ** 0.9 * (0.7 + 0.7 * n));
          shade = 0.5 + 0.5 * (1 - v * 0.4) * n;
        }
        const i = ((oy + y) * S * 4 + ox + x) * 4;
        img.data[i] = Math.min(255, shade * 255);
        img.data[i + 1] = Math.min(255, shade * 255);
        img.data[i + 2] = Math.min(255, shade * 255);
        img.data[i + 3] = Math.min(255, Math.max(0, a) * 255);
      }
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  return t;
}

export class ParticleSystem {
  readonly mesh: THREE.Mesh;
  private readonly pool: P[] = [];
  private readonly posSize: Float32Array;
  private readonly params: Float32Array;
  private readonly vel: Float32Array;
  private readonly aPosSize: THREE.InstancedBufferAttribute;
  private readonly aParams: THREE.InstancedBufferAttribute;
  private readonly aVel: THREE.InstancedBufferAttribute;
  private readonly tint: Float32Array;
  private readonly aTint: THREE.InstancedBufferAttribute;
  private readonly geo: THREE.InstancedBufferGeometry;
  private free: number[] = [];
  readonly material: THREE.ShaderMaterial;
  private static atlas: THREE.Texture | null = null;

  constructor(readonly capacity: number, additive: boolean) {
    const base = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = base.index;
    this.geo.setAttribute('position', base.getAttribute('position'));
    this.posSize = new Float32Array(capacity * 4);
    this.params = new Float32Array(capacity * 4);
    this.vel = new Float32Array(capacity * 3);
    this.tint = new Float32Array(capacity * 3);
    this.aTint = new THREE.InstancedBufferAttribute(this.tint, 3).setUsage(THREE.DynamicDrawUsage);
    this.aPosSize = new THREE.InstancedBufferAttribute(this.posSize, 4).setUsage(THREE.DynamicDrawUsage);
    this.aParams = new THREE.InstancedBufferAttribute(this.params, 4).setUsage(THREE.DynamicDrawUsage);
    this.aVel = new THREE.InstancedBufferAttribute(this.vel, 3).setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('aPosSize', this.aPosSize);
    this.geo.setAttribute('aParams', this.aParams);
    this.geo.setAttribute('aVel', this.aVel);
    this.geo.setAttribute('aTint', this.aTint);
    this.geo.instanceCount = 0;
    ParticleSystem.atlas ??= makeAtlas();
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        ...atmoUniforms,
        uAtlas: { value: ParticleSystem.atlas },
        uDepth: { value: null },
        uRes: { value: new THREE.Vector2(1, 1) },
        uNearFar: { value: new THREE.Vector2(0.25, 50000) },
        uAdditive: { value: additive ? 1 : 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = additive ? 20 : 10;
    for (let i = capacity - 1; i >= 0; i--) {
      this.free.push(i);
      this.pool.push({
        kind: PKind.Spray, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, age: 0, life: 1, size0: 1, size1: 1, alpha: 1, rot: 0, rotV: 0,
        drag: 0, gravity: 0, cell: 0, alive: false, tr: 1, tg: 1, tb: 1,
      });
    }
  }

  setDepth(depth: THREE.Texture, w: number, h: number, near: number, far: number): void {
    const u = this.material.uniforms;
    u.uDepth!.value = depth;
    u.uRes!.value.set(w, h);
    u.uNearFar!.value.set(near, far);
  }

  spawn(kind: PKind, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size0: number, size1: number, alpha: number, tint?: readonly number[]): void {
    const i = this.free.pop();
    if (i === undefined) return;
    const p = this.pool[i]!;
    p.kind = kind;
    p.x = x; p.y = y; p.z = z;
    p.vx = vx; p.vy = vy; p.vz = vz;
    p.age = 0;
    p.life = life;
    p.size0 = size0;
    p.size1 = size1;
    p.alpha = alpha;
    p.rot = Math.random() * Math.PI * 2;
    const smoky = kind === PKind.Smoke || kind === PKind.Soot;
    p.rotV = (Math.random() - 0.5) * (smoky ? 0.3 : kind === PKind.Debris ? 14 : kind === PKind.Flame ? 0.8 : 1.5);
    p.drag = smoky ? 1.3 : kind === PKind.Mist ? 0.8 : kind === PKind.Spark ? 0.4 : kind === PKind.Flame ? 2.2 : kind === PKind.Debris ? 0.12 : kind === PKind.Ember ? 1.0 : 0.9;
    p.gravity = kind === PKind.Smoke ? -0.4 : kind === PKind.Soot ? -0.9 : kind === PKind.Mist || kind === PKind.Flash ? 0 : kind === PKind.Flame ? -4.5 : kind === PKind.Ember ? -1.8 : 9.81;
    p.cell = kind === PKind.Smoke || kind === PKind.Mist ? 1 : kind === PKind.Flash || kind === PKind.Spark ? 3 : kind === PKind.Droplet ? 2
      : kind === PKind.Flame ? 4 : kind === PKind.Debris ? 5 : kind === PKind.Ember ? 6 : kind === PKind.Soot ? 7 : 0;
    const t = tint ?? TINTS[kind] ?? WHITE;
    p.tr = t[0]!;
    p.tg = t[1]!;
    p.tb = t[2]!;
    p.alive = true;
  }

  get active(): number {
    return this.capacity - this.free.length;
  }

  update(dt: number, windX: number, windZ: number, windSpeed: number, waterHeight: (x: number, z: number) => number): void {
    let n = 0;
    const ps = this.posSize, pa = this.params, pv = this.vel;
    for (let i = 0; i < this.capacity; i++) {
      const p = this.pool[i]!;
      if (!p.alive) continue;
      p.age += dt;
      if (p.age >= p.life) {
        p.alive = false;
        this.free.push(i);
        continue;
      }
      // Drag toward the wind's velocity.
      const k = 1 - Math.exp(-p.drag * dt);
      p.vx += (windX * windSpeed - p.vx) * k;
      p.vz += (windZ * windSpeed - p.vz) * k;
      p.vy -= p.gravity * dt;
      if (p.kind === PKind.Smoke || p.kind === PKind.Mist || p.kind === PKind.Soot) p.vy *= 1 - k * 0.5;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.rot += p.rotV * dt;
      if ((p.kind === PKind.Spray || p.kind === PKind.Droplet || p.kind === PKind.Spark || p.kind === PKind.Debris) && p.vy < 0 && (i & 3) === (n & 3)) {
        if (p.y < waterHeight(p.x, p.z)) {
          p.alive = false;
          this.free.push(i);
          continue;
        }
      }
      const t = p.age / p.life;
      const size = p.size0 + (p.size1 - p.size0) * Math.sqrt(t);
      let fade: number;
      if (p.kind === PKind.Flash || p.kind === PKind.Ember) fade = 1 - t;
      else if (p.kind === PKind.Smoke || p.kind === PKind.Soot) fade = Math.min(1, t * 10) * Math.pow(1 - t, 1.3);
      else if (p.kind === PKind.Flame) fade = Math.min(1, t * 8) * Math.pow(1 - t, 0.8);
      else if (p.kind === PKind.Debris) fade = t > 0.85 ? (1 - t) / 0.15 : 1;
      else if (p.kind === PKind.Mist) fade = Math.min(1, t * 6) * (1 - t) * (1 - t);
      else fade = Math.min(1, t * 10) * (1 - t);
      ps[n * 4] = p.x;
      ps[n * 4 + 1] = p.y;
      ps[n * 4 + 2] = p.z;
      ps[n * 4 + 3] = size;
      pa[n * 4] = p.alpha * fade;
      pa[n * 4 + 1] = p.rot;
      pa[n * 4 + 2] = p.cell;
      pa[n * 4 + 3] = p.kind === PKind.Droplet ? 0.06 : 0;
      pv[n * 3] = p.vx;
      pv[n * 3 + 1] = p.vy;
      pv[n * 3 + 2] = p.vz;
      this.tint[n * 3] = p.tr;
      this.tint[n * 3 + 1] = p.tg;
      this.tint[n * 3 + 2] = p.tb;
      n++;
    }
    this.geo.instanceCount = n;
    this.aPosSize.clearUpdateRanges();
    this.aPosSize.addUpdateRange(0, n * 4);
    this.aParams.clearUpdateRanges();
    this.aParams.addUpdateRange(0, n * 4);
    this.aVel.clearUpdateRanges();
    this.aVel.addUpdateRange(0, n * 3);
    this.aTint.clearUpdateRanges();
    this.aTint.addUpdateRange(0, n * 3);
    this.aTint.needsUpdate = true;
    this.aPosSize.needsUpdate = true;
    this.aParams.needsUpdate = true;
    this.aVel.needsUpdate = true;
  }
}
